/**
 * 프로젝트 삭제 확인 모달. cloud-api `DELETE /projects/:id` (OWNER/ADMIN 만).
 *
 * 안전장치: 사용자가 프로젝트 이름을 정확히 다시 입력해야 삭제 버튼 활성화.
 * 데스크톱 앱의 로컬 매핑은 별도로 정리해야 함 (cloud 삭제만 진행).
 */
import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { deleteProject, type Project } from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";

interface Props {
  open: boolean;
  onClose: () => void;
  onDeleted: (projectId: string) => void;
  project: Project;
}

export function DeleteProjectDialog({
  open,
  onClose,
  onDeleted,
  project,
}: Props): JSX.Element | null {
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setConfirmation("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const canDelete = confirmation === project.name;

  const onConfirm = async (): Promise<void> => {
    if (!canDelete) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteProject(project.id);
      onDeleted(project.id);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, "프로젝트 삭제에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden />
            </div>
            <div>
              <h2 className="text-lg font-semibold">프로젝트 삭제</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                이 작업은 되돌릴 수 없습니다.
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="닫기"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <p>
            <strong>{project.name}</strong> 프로젝트와 그에 속한 모든 태스크 /
            PR / 사용량 로그가 클라우드에서 삭제됩니다. 데스크톱 앱의 로컬
            폴더와 git 저장소는 그대로 남습니다.
          </p>

          <div>
            <label htmlFor="delete-confirm" className="text-sm font-medium">
              확인을 위해 프로젝트 이름{" "}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                {project.name}
              </code>{" "}
              을 입력하세요
            </label>
            <Input
              id="delete-confirm"
              type="text"
              autoFocus
              autoComplete="off"
              className="mt-1.5"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </div>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            취소
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={!canDelete || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            삭제
          </Button>
        </div>
      </div>
    </div>
  );
}
